import {
  faCaretDown, faCaretUp,
} from '@fortawesome/pro-solid-svg-icons';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  type FC, type HTMLAttributes, type ReactNode, useCallback, useEffect, useRef, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { MapOverlayDividerComponent } from '~/_shared/components/mapOverlay';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import { useScrollControls } from '~/_shared/components/scrollbar/useScrollControls.hook';
import { useResizeObserver } from '~/_shared/hooks/useResizeObserver';
import { isTouchOnly } from '~/_shared/utils/deviceDetect/deviceDetect.helpers';
import {
  arrowStyle, arrowsWrapperStyle, contentElementStyles, contentScrollbarStyle,
  fadeBottomStyle, fadeTopStyle, mapOverlayPanelBackgroundStyle, mapOverlayPanelWrapperStyles, paddedSectionStyles,
} from './mapOverlayPanel.styles';

const isTouchOnlyDevice = isTouchOnly();

type ScrollArrowPlacement = 'top' | 'bottom';

export type MapOverlayPanelProps = Readonly<{
  className?: string;
  content: ReactNode;
  prefix?: ReactNode;
  staticTop?: ReactNode;
  scrollArrowPlacement?: ScrollArrowPlacement;
}>;

export const MapOverlayPanelComponent: FC<MapOverlayPanelProps & Omit<HTMLAttributes<HTMLDivElement>, keyof MapOverlayPanelProps>> = ({
  prefix,
  content,
  staticTop,
  scrollArrowPlacement = 'top',
  ...restProps
}) => {
  const [isHeightAnimationInProgress, setIsHeightAnimationInProgress] = useState(false);
  const isHeightAnimationInProgressRef = useRef(isHeightAnimationInProgress);
  const [showScrollArrows, setShowScrollArrows] = useState(false);
  const animationStartHandler = useRef((event: TransitionEvent) => {
    if ((event.propertyName === 'height') && (
      (event.target as HTMLElement | null)?.matches('[data-overlayscrollbars-initialize]'))
    ) {
      setIsHeightAnimationInProgress(true);
      isHeightAnimationInProgressRef.current = true;
    }
  });
  const animationEndHandler = useRef((event: TransitionEvent) => {
    if ((event.propertyName === 'height') && (
      (event.target as HTMLElement | null)?.matches('[data-overlayscrollbars-initialize]'))
    ) {
      setTimeout(() => {
        // We need a bit of a delay to re-measure stuff before we potentially commit showing/hiding the arrows.
        setIsHeightAnimationInProgress(false);
        isHeightAnimationInProgressRef.current = false;
        updateScrollBoundariesRef.current();
      }, 100);
    }
  });
  const [isScrollableElementMounted, setIsScrollableElementMounted] = useState(false);
  const scrollbarRef = useRef<OverlayScrollbarsComponentRef | null>(null);
  const contentElementRef = useRef<HTMLDivElement | null>(null);
  const scrollableElementRef = useRef<HTMLDivElement | null>(null);
  const {
    atBottom, atTop, scrollDown, scrollUp, stopScrolling, updateScrollBoundaries, scrollYPossible,
  } = useScrollControls(scrollbarRef);
  const updateScrollBoundariesRef = useRef(updateScrollBoundaries);

  const stableUpdateScrollBoundaries = useCallback(() => {
    if (!isHeightAnimationInProgressRef.current) {
      updateScrollBoundariesRef.current();
    }
  }, []);

  const scrollableElementRefCallback = useCallback((element: HTMLDivElement) => {
    if (scrollableElementRef.current) {
      scrollableElementRef.current.removeEventListener('transitionstart', animationStartHandler.current);
      scrollableElementRef.current.removeEventListener('transitionend', animationEndHandler.current);
    }

    scrollableElementRef.current = element;
    setIsScrollableElementMounted(!!element);

    if (element) {
      element.addEventListener('transitionstart', animationStartHandler.current);
      element.addEventListener('transitionend', animationEndHandler.current);
    }
  }, []);

  const { height: contentHeight } = useResizeObserver({ ref: contentElementRef.current ? contentElementRef : null });

  useResizeObserver({
    ref: (isScrollableElementMounted && scrollableElementRef.current) ? scrollableElementRef : null,
    onResize: stableUpdateScrollBoundaries,
  });

  useEffect(() => {
    if (!isHeightAnimationInProgress && !isHeightAnimationInProgressRef.current) {
      setShowScrollArrows(scrollYPossible && !isTouchOnlyDevice);
    }
  }, [isHeightAnimationInProgress, scrollYPossible]);

  useEffect(() => {
    updateScrollBoundariesRef.current = updateScrollBoundaries;
  }, [updateScrollBoundaries]);

  const handleScrollUp = useCallback(() => {
    if (!atTop) {
      scrollUp();
    }
  }, [atTop, scrollUp]);

  const handleScrollDown = useCallback(() => {
    if (!atBottom) {
      scrollDown();
    }
  }, [atBottom, scrollDown]);

  return (
    <div
      css={mapOverlayPanelWrapperStyles}
      {...restProps}
    >
      {prefix}
      <div
        ref={scrollableElementRefCallback}
        css={mapOverlayPanelBackgroundStyle}
      >
        {staticTop}
        <div css={paddedSectionStyles}>
          {showScrollArrows && (scrollArrowPlacement === 'top') && (
            <>
              <div css={arrowsWrapperStyle}>
                <button
                  css={arrowStyle(atTop)}
                  onMouseDown={handleScrollUp}
                  onMouseUp={stopScrolling}
                  onMouseLeave={stopScrolling}
                >
                  <FontAwesomeIcon icon={faCaretUp} />
                </button>
                <button
                  css={arrowStyle(atBottom)}
                  onMouseDown={handleScrollDown}
                  onMouseUp={stopScrolling}
                  onMouseLeave={stopScrolling}
                >
                  <FontAwesomeIcon icon={faCaretDown} />
                </button>
              </div>

              <MapOverlayDividerComponent>
                <div css={!isTouchOnlyDevice && fadeTopStyle(!atTop, false)} />
              </MapOverlayDividerComponent>
            </>
          )}

          <div css={fadeTopStyle((isTouchOnlyDevice || (scrollArrowPlacement === 'bottom')) && !atTop, !staticTop)} />
          <OverlayScrollbarComponent
            isScrollbarHidden
            ref={scrollbarRef}
            onUpdate={updateScrollBoundaries}
            type={ScrollbarType.Vertical}
            css={contentScrollbarStyle}
            style={{ height: contentHeight ? contentHeight : undefined }}
          >
            <div
              css={contentElementStyles}
              ref={contentElementRef}
            >
              {content}
            </div>
          </OverlayScrollbarComponent>
          <div css={fadeBottomStyle((isTouchOnlyDevice || (scrollArrowPlacement === 'top')) && !atBottom, true)} />

          {showScrollArrows && (scrollArrowPlacement === 'bottom') && (
            <>
              <MapOverlayDividerComponent>
                <div css={!isTouchOnlyDevice && fadeBottomStyle(!atBottom, false)} />
              </MapOverlayDividerComponent>

              <div css={arrowsWrapperStyle}>
                <button
                  css={arrowStyle(atTop)}
                  onMouseDown={handleScrollUp}
                  onMouseUp={stopScrolling}
                  onMouseLeave={stopScrolling}
                >
                  <FontAwesomeIcon icon={faCaretUp} />
                </button>
                <button
                  css={arrowStyle(atBottom)}
                  onMouseDown={handleScrollDown}
                  onMouseUp={stopScrolling}
                  onMouseLeave={stopScrolling}
                >
                  <FontAwesomeIcon icon={faCaretDown} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
